/** UXP-00008 — Revenue Intelligence Platform framework (mirrors pkg/revenueintel) */

export const UXP_RI_ID = 'UXP-00008';

export interface CommandCenterKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface CampaignRow {
  name: string;
  type: string;
  budget: string;
  roas: string;
  status: string;
}

export interface KeywordGroup {
  category: string;
  keywords: string[];
}

export interface OrganicPaidRow {
  dimension: string;
  organic: number;
  paid: number;
  correlation: number;
}

export interface ForecastRow {
  label: string;
  current: string;
  forecast: string;
  confidence: number;
}

export interface OptimizationRec {
  type: string;
  title: string;
  impact: string;
  confidence: number;
}

export const COMMAND_CENTER_METRICS = [
  'Revenue', 'Profit', 'Advertising Revenue', 'Organic Revenue', 'Contribution Margin',
  'ROAS', 'ACOS', 'TACOS', 'Cash Flow', 'Forecast', 'Marketplace Comparison',
];

export const BUSINESS_GOALS = [
  'Revenue Growth', 'Profit Growth', 'Keyword Ranking', 'Brand Awareness', 'Product Launch',
  'Inventory Liquidation', 'Prime Day', 'Festival Campaigns', 'International Expansion', 'Market Share Growth',
];

export const CAMPAIGN_TYPES = [
  'Sponsored Products', 'Sponsored Brands', 'Sponsored Display', 'Sponsored Video',
  'Campaign Portfolios', 'Campaign Templates', 'Cross-marketplace Campaigns', 'Campaign Lifecycle',
];

export const CAMPAIGN_BUILDER_FEATURES = [
  'Campaign Structures', 'Campaign Objectives', 'Budget Allocation',
  'Naming Standards', 'Approval Workflow', 'Publishing Plans',
];

export const KEYWORD_TYPES = [
  'Primary Keywords', 'Secondary Keywords', 'Long-tail Keywords', 'Competitor Keywords',
  'Negative Keywords', 'Semantic Keywords', 'Seasonal Keywords', 'Search Intent', 'Keyword Clusters',
];

export const BID_BUDGET_FEATURES = [
  'Dynamic Bids', 'AI Bids', 'Placement Bids', 'Profit-based Bids',
  'Inventory-aware Bids', 'Budget Allocation', 'Cash Flow-aware Budgets',
];

export const ORGANIC_PAID_DIMENSIONS = [
  'Organic Rankings', 'Advertising Rankings', 'CTR', 'Conversion', 'Creative Performance',
  'Content Performance', 'Review Trends', 'Traffic', 'Revenue',
];

export const OPTIMIZATION_RECS = [
  'Campaign Changes', 'Keyword Changes', 'Creative Improvements', 'Content Improvements',
  'Budget Adjustments', 'Bid Changes', 'Product Optimizations', 'Inventory Adjustments',
];

export const COMPETITOR_DIMENSIONS = [
  'Keyword Overlap', 'Advertising Share', 'Creative Comparison', 'Pricing',
  'Reviews', 'Estimated Visibility', 'Estimated Spend',
];

export const FORECAST_METRICS = [
  'Revenue', 'Profit', 'ROAS', 'ACOS', 'Traffic', 'Orders', 'Inventory', 'Cash Flow', 'Seasonality',
];

export const EXPERIMENT_TYPES = [
  'Campaign Tests', 'Keyword Tests', 'Creative Tests', 'Budget Tests',
  'Placement Tests', 'Audience Tests', 'Expected ROI Simulations',
];

export const COLLABORATION_FEATURES = [
  'Reviews', 'Assignments', 'Mentions', 'Approvals', 'Marketing Notes', 'Decision History',
];

export const AI_LEARNING_SOURCES = [
  'Campaign Results', 'Revenue Outcomes', 'Creative Performance', 'Keyword Trends',
  'Brand DNA', 'Product DNA', 'Customer Intent DNA', 'Marketplace Changes',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Business Command Center', 'Business Goals', 'Campaign Management',
  'Keyword Intelligence', 'Bid & Budget Engine', 'Organic + Paid Correlation',
  'AI Optimization', 'Competitor Intelligence', 'Forecasting',
  'Experimentation', 'AI Learning Loop',
];

export const ANALYTICS_METRICS = [
  'Campaign Performance', 'Revenue Growth', 'Profitability', 'Keyword Performance',
  'Creative Performance', 'AI Adoption', 'Business Impact',
];

export const LIVE_ROUTES: Record<string, string> = {
  advertising: '/amazon/advertising',
  revenueOps: '/amazon/revenue-ops',
  growth: '/amazon/growth',
};

export const DEMO_KPIS: CommandCenterKPI[] = [
  { label: 'Revenue', value: '₹48.2L', change: '+12%', positive: true },
  { label: 'Profit', value: '₹14.6L', change: '+8%', positive: true },
  { label: 'ROAS', value: '4.2x', change: '+0.3x', positive: true },
  { label: 'TACOS', value: '11.2%', change: '-1.1%', positive: true },
  { label: 'ACOS', value: '24.8%', change: '-2.4%', positive: true },
  { label: 'Organic Revenue', value: '₹28.4L', change: '+15%', positive: true },
  { label: 'Ad Revenue', value: '₹19.8L', change: '+9%', positive: true },
  { label: 'Contribution Margin', value: '32%', change: '+2pp', positive: true },
  { label: 'Cash Flow', value: '₹9.1L', change: '+5%', positive: true },
  { label: 'Forecast (30d)', value: '₹52.8L', change: '+9%', positive: true },
  { label: 'Amazon.in vs .com', value: '62/38', change: 'stable', positive: true },
];

export const DEMO_CAMPAIGNS: CampaignRow[] = [
  { name: 'Earbuds Pro — Exact Match', type: 'Sponsored Products', budget: '₹45K/mo', roas: '5.1x', status: 'active' },
  { name: 'Brand Defense — SoundWave', type: 'Sponsored Brands', budget: '₹22K/mo', roas: '3.8x', status: 'active' },
  { name: 'Retargeting — Cart Abandoners', type: 'Sponsored Display', budget: '₹18K/mo', roas: '2.9x', status: 'active' },
  { name: 'Product Launch — Hub Gen 3', type: 'Sponsored Video', budget: '₹12K/mo', roas: '1.8x', status: 'learning' },
];

export const DEMO_KEYWORDS: KeywordGroup[] = [
  { category: 'Primary', keywords: ['wireless earbuds', 'bluetooth earphones', 'noise cancelling earbuds'] },
  { category: 'Long-tail', keywords: ['anc earbuds under 5000', 'best earbuds for gym india'] },
  { category: 'Competitor', keywords: ['competitor x pro', 'competitor y lite'] },
  { category: 'Negative', keywords: ['cheap', 'repair', 'used'] },
  { category: 'Seasonal', keywords: ['diwali gift earbuds', 'monsoon waterproof earbuds'] },
];

export const DEMO_ORGANIC_PAID: OrganicPaidRow[] = [
  { dimension: 'CTR', organic: 3, paid: 4, correlation: 0.72 },
  { dimension: 'Conversion', organic: 4, paid: 3, correlation: 0.68 },
  { dimension: 'Traffic', organic: 5, paid: 4, correlation: 0.81 },
  { dimension: 'Revenue', organic: 4, paid: 5, correlation: 0.76 },
];

export const DEMO_FORECASTS: ForecastRow[] = [
  { label: 'Revenue', current: '₹48.2L', forecast: '₹52.8L', confidence: 87 },
  { label: 'Profit', current: '₹14.6L', forecast: '₹16.1L', confidence: 82 },
  { label: 'ROAS', current: '4.2x', forecast: '4.5x', confidence: 79 },
  { label: 'ACOS', current: '24.8%', forecast: '23.1%', confidence: 81 },
  { label: 'Traffic', current: '842K', forecast: '920K', confidence: 85 },
];

export const DEMO_OPTIMIZATIONS: OptimizationRec[] = [
  { type: 'Bid Changes', title: 'Increase exact-match bid on top converter by 12%', impact: '+₹2.1L revenue/mo', confidence: 91 },
  { type: 'Budget Adjustments', title: 'Shift 15% budget from display to SP exact match', impact: '+0.4x ROAS', confidence: 84 },
  { type: 'Keyword Changes', title: 'Add 8 long-tail keywords from search term report', impact: '+18% sessions', confidence: 78 },
  { type: 'Creative Improvements', title: 'Test lifestyle hero in SB campaign — CTR +23% predicted', impact: '+8% conversion', confidence: 76 },
  { type: 'Inventory Adjustments', title: 'Pause ads on 3 SKUs below reorder point', impact: 'Prevent stockout waste', confidence: 95 },
];

export function buildRevenueIntelDashboard() {
  return {
    command_center_metrics: COMMAND_CENTER_METRICS.length,
    business_goals: BUSINESS_GOALS.length,
    campaign_types: CAMPAIGN_TYPES.length,
    keyword_types: KEYWORD_TYPES.length,
    bid_budget_features: BID_BUDGET_FEATURES.length,
    organic_paid_dimensions: ORGANIC_PAID_DIMENSIONS.length,
    optimization_recs: OPTIMIZATION_RECS.length,
    forecast_metrics: FORECAST_METRICS.length,
  };
}
